import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calendar, Clock, Award, BookOpen, Users, Star, ChevronRight, Menu, X, Brain, Shield, Heart, FileText, ExternalLink, Globe, Check, Download, AlertTriangle, Linkedin, ChevronDown } from "lucide-react";
import BookingFlow from "@/components/BookingFlow";
import WarningSignsQuiz from "@/components/WarningSignsQuiz";
import OptionsComparison from "@/components/OptionsComparison";
import { track, attribution, identifyUser, landingVariant, tagReviewClick } from "@/lib/analytics";
import { HERO_VARIANTS } from "@/lib/heroVariants";
import { HeroTrustStrip } from "@/components/HeroTrustStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GUIDE_FALLBACK_URL =
  "https://customer-assets.emergentagent.com/job_boss-clinic/artifacts/v46cefyt_5-WARNING-SIGNS-YOUR-BOSS-SCORES-HIGH-ON-THE-DARK-TRIAD-TRAITS.pdf";

const LandingPage = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [datasetStats, setDatasetStats] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/dataset/stats`)
      .then((res) => setDatasetStats(res.data))
      .catch(() => setDatasetStats(null));
    axios
      .get(`${API}/reviews/public`)
      .then((res) => setReviews(res.data?.reviews || []))
      .catch(() => setReviews([]));
    // Auto-open booking flow if arriving from the no-show recovery email link (?rebook={old_booking_id})
    const params = new URLSearchParams(window.location.search);
    if (params.get("rebook") || params.get("book")) setShowBooking(true);
  }, []);

  // Reveal the sticky mobile booking bar once the hero scrolls out of view.
  useEffect(() => {
    const onScroll = () => setShowStickyCta(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openBooking = (source) => {
    track("booking_opened", { source });
    setShowBooking(true);
  };

  const openQuiz = (source) => {
    track("quiz_opened", { source });
    setShowQuiz(true);
  };

  const variant = landingVariant();
  const hero = HERO_VARIANTS[variant] || HERO_VARIANTS.default;
  const featuredReview =
    reviews.find((r) => r.is_featured && r.comment) || reviews.find((r) => r.comment) || null;

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const runHeroAction = (cta, position) => {
    if (cta.action === "book") return openBooking(`hero_${position}_${variant}`);
    if (cta.action === "quiz") return openQuiz(`hero_${position}_${variant}`);
    track("hero_scroll_cta", { target: cta.action, variant });
    scrollTo({ about: "about", how: "how-it-works", services: "services" }[cta.action] || "about");
  };

  const [guideEmail, setGuideEmail] = useState("");
  const [guideState, setGuideState] = useState("idle");
  const [guideError, setGuideError] = useState("");

  const submitGuide = async (e) => {
    e.preventDefault();
    setGuideError("");
    setGuideState("sending");
    try {
      const res = await axios.post(`${API}/guide-requests`, {
        email: guideEmail.trim(),
        origin_url: window.location.origin,
        attribution: attribution(),
      });
      identifyUser({ email: guideEmail.trim().toLowerCase() });
      track("guide_requested", { guide: "5_warning_signs", variant });
      window.open(res.data.guide_url, "_blank", "noopener");
      setGuideState("sent");
    } catch (error) {
      setGuideError("That didn't go through. Check the address and try again.");
      setGuideState("idle");
    }
  };

  const faqs = [
    {
      question: "Is this therapy or coaching?",
      answer: "No. The Bad Boss Clinic provides science-based behavioral strategies, not therapy or coaching. Our behavioural scientists translate academic research into practical strategies you can implement immediately. If you're experiencing mental health issues, we recommend consulting a licensed therapist."
    },
    {
      question: "What happens in an initial consultation?",
      answer: "Before your session, you'll complete a pre-session assessment that evaluates your leader's behavior and your personality profile. During the 60-minute consultation, a Bad Boss Clinic behavioural scientist will analyse your specific situation, explain the psychological dynamics at play, and provide personalized, evidence-based strategies tailored to your circumstances."
    },
    {
      question: "How should I prepare for my session?",
      answer: "Complete the pre-session assessment form (sent after booking) at least 24 hours before your appointment. Think about specific examples of your boss's behavior and any coping strategies you've already tried. The more detail you can provide, the more tailored your strategies will be."
    },
    {
      question: "What if my boss finds out?",
      answer: "Your consultation is completely confidential. We don't contact employers or share any information. The strategies provided are designed to be implemented subtly without confrontation—you'll learn to navigate the situation while protecting yourself."
    },
    {
      question: "Do I need an initial session, or can I book a follow-up directly?",
      answer: "All new clients must start with an Initial Consultation (60 min, €299). This comprehensive session establishes your baseline and develops your personalized strategy. Follow-up sessions (30 min, €149) are only available after completing an initial consultation and are designed for ongoing support and strategy refinement."
    },
    {
      question: "What types of 'bad boss' behaviors do you address?",
      answer: "We specialize in leaders exhibiting Dark Triad traits: narcissism (grandiosity, need for admiration), Machiavellianism (manipulation, deceit), and psychopathy (lack of empathy, callousness). This includes micromanagement, gaslighting, credit-stealing, public humiliation, favoritism, and emotional manipulation."
    },
    {
      question: "Can you guarantee results?",
      answer: "While we can't guarantee specific outcomes (every situation is unique), our strategies are grounded in peer-reviewed research and have been refined through working with thousands of people. Most clients report feeling more empowered and less stressed after implementing the strategies."
    },
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-serif text-xl md:text-2xl font-semibold text-white tracking-tight">
              Bad Boss <span className="text-[#D4AF37]">Clinic</span>
            </span>
          </div>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-slate-400 hover:text-white transition-colors text-sm uppercase tracking-widest">About</a>
            <a href="#how-it-works" className="text-slate-400 hover:text-white transition-colors text-sm uppercase tracking-widest">How It Works</a>
            <a href="#services" className="text-slate-400 hover:text-white transition-colors text-sm uppercase tracking-widest">Services</a>
            <a href="#faq" className="text-slate-400 hover:text-white transition-colors text-sm uppercase tracking-widest">FAQ</a>
            <Link to="/portal/login" className="text-slate-400 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-widest" data-testid="header-portal-link">Client Portal</Link>
            <Button 
              data-testid="header-book-btn"
              onClick={() => openBooking("header")}
              className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium px-6 py-2 rounded-none uppercase tracking-widest text-xs"
            >
              Book Now
            </Button>
          </nav>

          {/* Mobile menu button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800 px-6 py-4">
            <nav className="flex flex-col gap-4">
              <a href="#about" className="text-slate-400 hover:text-white transition-colors text-sm uppercase tracking-widest">About</a>
              <a href="#how-it-works" className="text-slate-400 hover:text-white transition-colors text-sm uppercase tracking-widest">How It Works</a>
              <a href="#services" className="text-slate-400 hover:text-white transition-colors text-sm uppercase tracking-widest">Services</a>
              <a href="#faq" className="text-slate-400 hover:text-white transition-colors text-sm uppercase tracking-widest">FAQ</a>
              <Link to="/portal/login" onClick={() => setMobileMenuOpen(false)} className="text-slate-400 hover:text-[#D4AF37] transition-colors text-sm uppercase tracking-widest" data-testid="mobile-portal-link">Client Portal</Link>
              <Button 
                data-testid="mobile-book-btn"
                onClick={() => { openBooking("mobile_menu"); setMobileMenuOpen(false); }}
                className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium px-6 py-3 rounded-none uppercase tracking-widest text-xs w-full"
              >
                Book Now
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-bg min-h-screen flex items-center pt-20">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 w-full min-w-0">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7 animate-fadeInUp">
              <p className="text-[#38BDF8] text-sm uppercase tracking-[0.3em] mb-4" data-testid="hero-eyebrow">{hero.eyebrow}</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal text-white leading-tight mb-6" data-testid="hero-headline">
                {hero.headline}
              </h1>
              <p className="text-slate-400 text-lg md:text-xl max-w-xl mb-8 leading-relaxed" data-testid="hero-subhead">
                {hero.subhead}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  data-testid="hero-book-btn"
                  onClick={() => runHeroAction(hero.primary, "primary")}
                  className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium px-8 py-6 rounded-none uppercase tracking-widest text-sm btn-primary gold-glow"
                >
                  {hero.primary.label}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline"
                  data-testid="hero-secondary-btn"
                  className="bg-transparent border border-slate-700 text-slate-200 hover:border-slate-500 hover:text-white hover:bg-transparent px-8 py-6 rounded-none uppercase tracking-widest text-sm"
                  onClick={() => runHeroAction(hero.secondary, "secondary")}
                >
                  {hero.secondary.label}
                </Button>
              </div>
              <HeroTrustStrip
                stats={datasetStats}
                review={featuredReview}
                onQuoteClick={(review) => {
                  tagReviewClick(review.review_id);
                  track("testimonial_clicked", { review_id: review.review_id, source: "hero_trust_strip" });
                  if (review.is_featured) {
                    axios.post(`${API}/reviews/${review.review_id}/click?cta=book`).catch(() => null);
                  }
                  openBooking("hero_trust_quote");
                }}
              />
            </div>
            
            <div className="md:col-span-5 mt-10 md:mt-0">
              <div className="relative animate-fadeInUp" style={{ animationDelay: "0.2s" }}>
                <div className="absolute -inset-4 bg-gradient-to-br from-[#D4AF37]/15 to-transparent blur-3xl" />
                <div className="relative bg-slate-900/60 border border-slate-800 backdrop-blur-md" data-testid="hero-premium-card">
                  <div className="p-7 border-b border-slate-800">
                    <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2 py-1 mb-4">
                      <Star className="w-2.5 h-2.5" /> Most popular way to start
                    </span>
                    <div className="flex items-baseline justify-between gap-4">
                      <h3 className="font-serif text-2xl text-white">Premium</h3>
                      <p className="whitespace-nowrap">
                        <span className="font-serif text-3xl text-[#D4AF37]" data-testid="hero-premium-price">€19</span>
                        <span className="text-slate-500 text-sm">/mo</span>
                      </p>
                    </div>
                    <p className="text-slate-500 text-[11px] mt-2 uppercase tracking-[0.18em]">
                      Cancel anytime · or €149/year
                    </p>
                  </div>
                  <div className="p-7 space-y-3.5">
                    {[
                      <>The full client portal, <strong className="text-white">unlocked</strong></>,
                      <>Big Five + Leader <strong className="text-white">Dark Triad</strong> assessments</>,
                      <><strong className="text-white">Unlimited</strong> private incident log</>,
                      <>Priority <strong className="text-white">AI advisor</strong> chat &amp; full vulnerability report</>,
                    ].map((line, idx) => (
                      <p key={idx} className="flex items-start gap-3 text-slate-300 text-sm leading-relaxed">
                        <Check className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
                        <span>{line}</span>
                      </p>
                    ))}
                  </div>
                  <div className="p-7 border-t border-slate-800 space-y-4">
                    <Link
                      to="/portal/register?checkout=premium_monthly"
                      onClick={() => track("premium_cta_clicked", { source: "hero_card", variant })}
                      data-testid="hero-premium-start-btn"
                      className="flex items-center justify-center gap-2 w-full bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium py-3 uppercase tracking-widest text-xs transition-colors"
                    >
                      Start with Premium
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => openBooking("hero_card")}
                      data-testid="hero-premium-book-link"
                      className="block w-full text-center text-slate-400 text-[11px] uppercase tracking-[0.2em] hover:text-[#D4AF37] transition-colors"
                    >
                      Prefer 1-on-1? Book a consultation →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 md:py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <div className="sticky top-32">
                <p className="text-[#38BDF8] text-sm uppercase tracking-[0.3em] mb-4">About the Clinic</p>
                <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
                  Decoding the <span className="text-[#D4AF37]">Dark Side</span> of Leadership
                </h2>
                <div
                  className="mt-8 hidden md:block border border-slate-800 bg-slate-900/50 divide-y divide-slate-800"
                  data-testid="clinic-credentials"
                >
                  {[
                    { value: "40+", label: "Peer-reviewed publications behind the method" },
                    {
                      value: datasetStats?.cases
                        ? datasetStats.cases.toLocaleString()
                        : "3,500+",
                      label: "Documented cases in the clinic's BADBOSS research programme",
                    },
                    {
                      value: datasetStats?.countries ? String(datasetStats.countries) : "41",
                      label: "Countries represented in the dataset",
                    },
                  ].map((stat) => (
                    <div key={stat.label} className="p-6" data-testid={stat.testid}>
                      <p className="font-serif text-3xl text-[#D4AF37] leading-none">{stat.value}</p>
                      <p className="text-slate-400 text-sm mt-2 leading-relaxed">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="space-y-6 text-slate-300 leading-relaxed">
                <p className="text-lg">
                  The Bad Boss Clinic is a behavioural science practice that combines 
                  <span className="text-white font-medium"> AI and psychological profiling</span> to 
                  unmask the "Dark Side" of leadership. Our research on <span className="text-white font-medium">Dark Triad traits</span>—Narcissism, 
                  Machiavellianism, and Psychopathy—gives you the clinical vocabulary to identify 
                  exactly why a leader is toxic.
                </p>
                <p>
                  The clinic's pioneering work on <span className="text-white font-medium">anxiety contagion</span> reveals how 
                  a leader's internal state "infects" their team. Your stress might not be "your" stress—it could be 
                  a byproduct of your boss's psychological state.
                </p>
                <p>
                  Unlike traditional consultants, we use <span className="text-white font-medium">Machine Learning 
                  and Big Data</span> to analyse leadership traits at scale, and we apply <span className="text-white font-medium">Attachment Theory</span> to 
                  explain why certain employees clash with certain bosses.
                </p>
                <p>
                  The research behind the clinic is featured in <span className="text-white font-medium">Harvard Business Review</span>,{" "}
                  <span className="text-white font-medium">Personality and Social Psychology Bulletin</span> and{" "}
                  <span className="text-white font-medium">Personality and Individual Differences</span>, where our
                  lead scientist also serves as <span className="text-[#38BDF8]">Associate Editor</span>.
                </p>
                
                {/* BADBOSS Survey Callout - Integrated */}
                <div className="bg-slate-900/50 border border-[#D4AF37]/30 p-6 mt-8">
                  <div className="flex items-start gap-4">
                    <Globe className="w-8 h-8 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-serif text-lg text-white mb-2">The BADBOSS Survey</h3>
                      <p className="text-slate-400 text-sm mb-3">
                        The clinic runs the <span className="text-white">Behavioral Analysis & Dynamics of Boss Outcomes & Staff Strategies (BADBOSS)</span> Survey—a 
                        global research initiative with <span className="text-[#D4AF37] font-medium">
                          {datasetStats?.cases ? `${datasetStats.cases.toLocaleString()} documented cases across ${datasetStats.countries} countries` : "3,500+ participants across 41 countries"}
                        </span> sharing 
                        experiences with toxic bosses and which coping strategies actually work.
                      </p>
                      <a
                        href="https://mubusiness.eu.qualtrics.com/jfe/form/SV_1U1xtUcyp9FKqrA"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#B5952F] text-sm transition-colors"
                      >
                        Contribute to the research
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-900/50 border border-slate-800 p-6 mt-4">
                  <p className="text-[#D4AF37] font-serif text-xl italic">
                    "At the Bad Boss Clinic, we don't do therapy. We don't do coaching. We provide science-based strategies you can apply today."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client testimonials */}
      {reviews.length > 0 && (
        <section className="py-16 md:py-20 px-6 bg-slate-900/50" data-testid="client-testimonials">
          <div className="max-w-6xl mx-auto">
            <p className="text-[#D4AF37] text-sm uppercase tracking-[0.3em] mb-4">From the couch</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 max-w-2xl">
              What clients say <span className="text-slate-500">after their session</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.map((r) => (
                <a
                  key={r.review_id}
                  href="#services"
                  onClick={() => {
                    tagReviewClick(r.review_id);
                    track("testimonial_clicked", { review_id: r.review_id, featured: !!r.is_featured });
                    if (r.is_featured) {
                      axios.post(`${API}/reviews/${r.review_id}/click?cta=book`).catch(() => null);
                    }
                  }}
                  className={`border p-6 flex flex-col transition-colors hover:border-[#D4AF37]/60 ${
                    r.is_featured ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.04]" : "border-slate-800 bg-slate-950/60"
                  }`}
                  data-testid={`testimonial-${r.review_id}`}
                >
                  {r.is_featured && (
                    <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">📌 Featured</span>
                  )}
                  <div className="flex gap-0.5 mb-4">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        size={14}
                        className={n <= r.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-slate-700"}
                      />
                    ))}
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed italic mb-4 flex-1">"{r.comment}"</p>
                  <p className="text-slate-500 text-xs uppercase tracking-widest">
                    {(r.client_name || "Client").split(" ")[0]} · {r.session_type === "initial" ? "Initial consultation" : "Follow-up"}
                  </p>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Science Credibility Strip */}
      <section id="research" className="py-16 md:py-24 px-6 bg-slate-900/30" data-testid="science-strip">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#38BDF8] text-sm uppercase tracking-[0.3em] mb-4">The Science</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6 max-w-2xl">
            Every strategy starts as <span className="text-[#D4AF37]">peer-reviewed research</span>
          </h2>
          <p className="text-slate-400 max-w-2xl leading-relaxed">
            The clinic's method is built on more than 40 peer-reviewed studies of dark triad leadership, stress
            contagion and attachment at work — not opinion, and not generic management advice.
          </p>

          <div className="mt-10 max-w-3xl" data-testid="science-outlets">
            <p className="text-slate-400 leading-relaxed">
              Published and featured in <span className="text-white font-medium">Harvard Business Review</span>,{" "}
              <span className="text-white font-medium">Personality and Individual Differences</span> and{" "}
              <span className="text-white font-medium">Personality and Social Psychology Bulletin</span>.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 md:py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#38BDF8] text-sm uppercase tracking-[0.3em] mb-4">The Process</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              How It <span className="text-[#D4AF37]">Works</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <span className="font-serif text-2xl text-[#D4AF37]">1</span>
              </div>
              <h3 className="font-serif text-xl text-white mb-3">Book Your Session</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Schedule your Initial Consultation (60 min). Choose a time that works for you and complete the secure payment.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <span className="font-serif text-2xl text-[#D4AF37]">2</span>
              </div>
              <h3 className="font-serif text-xl text-white mb-3">Complete Assessment</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Fill out the pre-session assessment to evaluate your boss's behavior and your personality profile. Receive instant results.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <span className="font-serif text-2xl text-[#D4AF37]">3</span>
              </div>
              <h3 className="font-serif text-xl text-white mb-3">Get Your Strategies</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                During your session, receive personalized, science-based strategies tailored to your specific situation that you can implement immediately.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button 
              data-testid="how-it-works-book-btn"
              onClick={() => openBooking("how_it_works")}
              className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium px-8 py-6 rounded-none uppercase tracking-widest text-sm"
            >
              Get Started
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section with Comparison Table */}
      <section id="services" className="clinic-bg py-16 md:py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#38BDF8] text-sm uppercase tracking-[0.3em] mb-4">Services</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              What the Bad Boss Clinic <span className="text-[#D4AF37]">Can Do for You</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              A 1-on-1 consultation is the flagship. If you'd rather start on your own, the client portal gives you the
              same assessments and research base at your own pace.
            </p>
          </div>

          {/* Full options comparison */}
          <div className="mb-12" data-testid="self-serve-products">
            <OptionsComparison onBook={() => openBooking("comparison_table")} />
          </div>

          {/* Important Note */}
          <div className="max-w-2xl mx-auto bg-slate-900/50 border border-slate-800 p-6 text-center">
            <AlertTriangle className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
            <p className="text-slate-300 text-sm">
              <strong className="text-white">Important:</strong> All new clients must complete an Initial Consultation before booking follow-up sessions. 
              This ensures your behavioural scientist has a complete understanding of your situation to provide effective guidance.
            </p>
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-400 text-sm max-w-2xl mx-auto leading-relaxed">
              Portal plans and consultation sessions are separate — many clients use both: the portal to track what
              happens week to week, a session when a decision really matters.{" "}
              <Link to="/portal/login" className="text-[#D4AF37] hover:underline" data-testid="products-login-link">
                Sign in to the portal
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section id="expertise" className="py-16 md:py-24 px-6 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#38BDF8] text-sm uppercase tracking-[0.3em] mb-4">Expertise</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              What Makes the Clinic <span className="text-[#D4AF37]">Different</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-900/50 border border-slate-800 p-8 hover:border-slate-700 transition-colors">
              <Brain className="w-10 h-10 text-[#D4AF37] mb-4" />
              <h3 className="font-serif text-xl text-white mb-3">Dark Triad Expert</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Provides the clinical vocabulary to identify exactly why a leader is toxic—the first step in developing effective coping strategies.
              </p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 p-8 hover:border-slate-700 transition-colors">
              <Heart className="w-10 h-10 text-[#D4AF37] mb-4" />
              <h3 className="font-serif text-xl text-white mb-3">Stress Contagion Science</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Helps you understand that your stress might not be "your" stress—it could be a byproduct of your leader's psychological state.
              </p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 p-8 hover:border-slate-700 transition-colors">
              <Shield className="w-10 h-10 text-[#D4AF37] mb-4" />
              <h3 className="font-serif text-xl text-white mb-3">Relational Dynamics Expert</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Applies attachment theory to explain why you clash with certain bosses and provides personalized strategies based on your unique relational style.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Signs Quiz Section */}
      <section className="py-16 md:py-24 px-6 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#38BDF8] text-sm uppercase tracking-[0.3em] mb-4">Free Assessment</p>
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
                Is Your Boss <span className="text-[#D4AF37]">Toxic</span>?
              </h2>
              <p className="text-slate-300 mb-6 leading-relaxed">
                Take our quick 7-question assessment based on Dark Triad research to identify warning signs 
                of narcissistic, Machiavellian, or psychopathic traits in your boss's behavior.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-400">
                  <Check className="w-5 h-5 text-[#D4AF37]" />
                  <span>Takes less than 2 minutes</span>
                </li>
                <li className="flex items-center gap-3 text-slate-400">
                  <Check className="w-5 h-5 text-[#D4AF37]" />
                  <span>Instant results with explanations</span>
                </li>
                <li className="flex items-center gap-3 text-slate-400">
                  <Check className="w-5 h-5 text-[#D4AF37]" />
                  <span>No email required to see your results</span>
                </li>
              </ul>
              <Button 
                data-testid="take-quiz-btn"
                onClick={() => openQuiz("quiz_section")}
                className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none px-8 py-6 uppercase tracking-widest text-sm"
              >
                Take the Quiz
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 p-8">
              <Download className="w-12 h-12 text-[#D4AF37] mb-6" />
              <h3 className="font-serif text-xl text-white mb-4">Free Download</h3>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                Get our comprehensive guide: <strong className="text-white">"5 Warning Signs Your Boss Has Dark Triad Traits"</strong> — 
                learn to identify narcissistic, Machiavellian, and psychopathic behaviors in leadership.
              </p>
              {guideState === "sent" ? (
                <div data-testid="guide-success">
                  <p className="text-[#D4AF37] text-sm font-medium mb-2">Guide sent to {guideEmail.trim()}</p>
                  <p className="text-slate-500 text-xs leading-relaxed mb-4">
                    It should have opened in a new tab as well. If your browser blocked it, use the link below.
                  </p>
                  <a
                    href={GUIDE_FALLBACK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="guide-open-link"
                    onClick={() => track("guide_downloaded", { guide: "5_warning_signs", variant })}
                    className="inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-widest hover:underline"
                  >
                    Open the guide
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              ) : (
                <form onSubmit={submitGuide} data-testid="guide-gate">
                  <label className="block text-slate-500 text-xs uppercase tracking-widest mb-2" htmlFor="guide-email">
                    Where should we send it?
                  </label>
                  <input
                    id="guide-email"
                    type="email"
                    required
                    value={guideEmail}
                    onChange={(e) => setGuideEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="guide-email-input"
                    className="w-full bg-slate-950 border border-slate-700 px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:border-[#D4AF37] focus:outline-none transition-colors"
                  />
                  <Button
                    type="submit"
                    disabled={guideState === "sending"}
                    data-testid="guide-submit-btn"
                    className="mt-4 w-full bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-slate-950 rounded-none px-6 py-3 h-auto uppercase tracking-widest text-xs transition-all disabled:opacity-60"
                  >
                    {guideState === "sending" ? "Sending…" : "Send me the guide"}
                  </Button>
                  {guideError && (
                    <p className="text-red-400 text-xs mt-3" data-testid="guide-error">{guideError}</p>
                  )}
                  <p className="text-slate-600 text-xs mt-3">
                    The guide opens straight away and lands in your inbox. No newsletter.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#38BDF8] text-sm uppercase tracking-[0.3em] mb-4">Questions</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              Frequently Asked <span className="text-[#D4AF37]">Questions</span>
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem 
                key={idx} 
                value={`item-${idx}`}
                className="bg-slate-900/50 border border-slate-800 px-6"
              >
                <AccordionTrigger className="text-left text-white hover:text-[#D4AF37] py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 pb-6 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-6 bg-slate-900/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-6">
            Ready to take <span className="text-[#D4AF37]">control</span>?
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Book a consultation and get science-based strategies you can implement today.
          </p>
          <Button 
            data-testid="cta-book-btn"
            onClick={() => openBooking("closing_cta")}
            className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] font-medium px-12 py-6 rounded-none uppercase tracking-widest text-sm btn-primary gold-glow"
          >
            Book Your Consultation
            <Calendar className="ml-3 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <h2 className="font-serif text-3xl text-white mb-4">
                Bad Boss <span className="text-[#D4AF37]">Clinic</span>
              </h2>
              <p className="text-slate-500 text-sm leading-relaxed">
                Science-based strategies for navigating toxic leadership in the workplace.
              </p>
              <p className="text-slate-600 text-xs mt-4 uppercase tracking-[0.2em]" data-testid="footer-entity-line">
                A service of Apex Advisory
              </p>
            </div>
            <div>
              <h3 className="text-white font-medium mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><a href="#about" className="text-slate-400 hover:text-[#D4AF37] text-sm transition-colors">About the Clinic</a></li>
                <li><a href="#services" className="text-slate-400 hover:text-[#D4AF37] text-sm transition-colors">Services</a></li>
                <li><a href="#faq" className="text-slate-400 hover:text-[#D4AF37] text-sm transition-colors">FAQ</a></li>
                <li><a href="#research" className="text-slate-400 hover:text-[#D4AF37] text-sm transition-colors">The Science</a></li>
              </ul>
              <h3 className="text-white font-medium mb-4 mt-8">Legal</h3>
              <ul className="space-y-2" data-testid="footer-legal-links">
                <li><a href="/privacy" className="text-slate-400 hover:text-[#D4AF37] text-sm transition-colors" data-testid="footer-privacy-link">Privacy Policy</a></li>
                <li><a href="/terms" className="text-slate-400 hover:text-[#D4AF37] text-sm transition-colors" data-testid="footer-terms-link">Terms of Service</a></li>
                <li><a href="/refunds" className="text-slate-400 hover:text-[#D4AF37] text-sm transition-colors" data-testid="footer-refunds-link">Refunds &amp; Cancellation</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-medium mb-4">Connect</h3>
              <a
                href="https://www.linkedin.com/company/the-badboss-survey/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-slate-400 hover:text-[#D4AF37] transition-colors"
              >
                <Linkedin className="w-5 h-5" />
                <span className="text-sm">LinkedIn</span>
              </a>
              <div className="mt-8">
                <p className="text-slate-600 text-xs leading-relaxed" data-testid="footer-copyright">
                  © 2026 Apex Advisory. All rights reserved.
                  <br />
                  Bad Boss Clinic is a service of Apex Advisory. Payments and invoices are issued by Apex Advisory.
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      <Dialog open={showBooking} onOpenChange={setShowBooking}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-800 text-white p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="font-serif text-2xl">
              Book Your <span className="text-[#D4AF37]">Consultation</span>
            </DialogTitle>
          </DialogHeader>
          <BookingFlow onClose={() => setShowBooking(false)} />
        </DialogContent>
      </Dialog>

      {/* Warning Signs Quiz Modal */}
      <Dialog open={showQuiz} onOpenChange={setShowQuiz}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-800 text-white p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="font-serif text-2xl">
              Is Your Boss <span className="text-[#D4AF37]">Toxic</span>?
            </DialogTitle>
          </DialogHeader>
          <WarningSignsQuiz onClose={() => setShowQuiz(false)} onBookSession={() => { setShowQuiz(false); openBooking("quiz_results"); }} />
        </DialogContent>
      </Dialog>
      {/* Spacer so the fixed mobile CTA never hides footer content */}
      <div className="h-20 md:hidden" aria-hidden="true" />

      {/* Sticky mobile booking CTA */}
      <div
        data-testid="sticky-mobile-cta"
        className={`md:hidden fixed inset-x-0 bottom-0 z-40 glass border-t border-slate-800 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] transition-transform duration-300 ${
          showStickyCta ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-white text-sm font-medium leading-tight truncate">Is your boss toxic?</p>
            <p className="text-slate-400 text-xs">
              Free 2-min test · or{" "}
              <Link
                to="/portal/register?checkout=premium_monthly"
                onClick={() => track("premium_cta_clicked", { source: "sticky_mobile_cta", variant })}
                data-testid="sticky-mobile-premium-link"
                className="text-slate-300 underline underline-offset-2 hover:text-[#D4AF37] transition-colors"
              >
                go Premium €19
              </Link>
            </p>
          </div>
          <button
            onClick={() => openQuiz("sticky_mobile_cta")}
            data-testid="sticky-mobile-quiz-btn"
            className="shrink-0 bg-[#D4AF37] text-slate-950 px-5 py-2.5 text-xs uppercase tracking-[0.16em] font-medium hover:bg-[#B5952F] transition-colors"
          >
            Take the Test
          </button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
